import { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { useParams, useLocation } from 'react-router';
import MarkdownRenderer from './MarkdownRenderer';
import TranslateOverlay from './TranslateOverlay';
import type { Section } from './types';

const API_BASE = 'https://api.podus.ai';

export default function DocumentPage() {
    const { documentId } = useParams();
    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [documentTitle, setDocumentTitle] = useState('Document');
    const scrollContainerRef = useRef<HTMLDivElement>(null);

    const [sections, setSections] = useState<Section[]>([]);
    const [queryLanguage, setQueryLanguage] = useState<string | null>(null);
    const [showTranslateOverlay, setShowTranslateOverlay] = useState(false);
    const [overlayTranslations, setOverlayTranslations] = useState<Record<string, string> | string>({});
    const [overlayLoading, setOverlayLoading] = useState(false);
    const [overlayError, setOverlayError] = useState<string | null>(null);
    const [overlayClosed, setOverlayClosed] = useState(false);
    const sectionRefs = useRef<Record<string, HTMLDivElement | null>>({});

    useEffect(() => {
        const fetchDocumentData = async () => {
            try {
                setLoading(true);

                const section = queryParams.get('section');
                const cluster = queryParams.get('cluster');
                const type = queryParams.get('type');

                const params: Record<string, string> = {};
                if (section) params.section = section;
                if (cluster) params.cluster = cluster;
                if (type) params.type = type;

                const { data } = await axios.get(`${API_BASE}/document/${documentId}`, {
                    params: Object.keys(params).length ? params : undefined,
                });

                if (data.data) {
                    setSections(data.data);
                    setDocumentTitle(data.title);

                    const targetSection = queryParams.get('section');
                    if (targetSection && data.data.length > 0) {
                        setTimeout(() => {
                            const targetElement = sectionRefs.current[targetSection];
                            if (targetElement) {
                                targetElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
                            }
                        }, 100);
                    }
                } else {
                    throw new Error(data.message || 'Failed to fetch document data');
                }
            } catch (err) {
                console.error('Error fetching document data:', err);
                setError(err instanceof Error ? err.message : 'An error occurred');
            } finally {
                setLoading(false);
            }
        };

        if (documentId) {
            fetchDocumentData();
        }
    }, [documentId, location.search]);

    useEffect(() => {
        const lang = queryParams.get('language');
        setQueryLanguage(lang || 'english');
        setShowTranslateOverlay(true);
        setOverlayClosed(false);
    }, [location.search]);

    useEffect(() => {
        const targetSectionId = queryParams.get('section');
        if (showTranslateOverlay && sections.length && targetSectionId) {
            const sectionToTranslate = sections.find((s) => s.section == targetSectionId);
            if (sectionToTranslate) {
                if (!queryLanguage || queryLanguage.toLowerCase() === 'english') {
                    setOverlayTranslations(sectionToTranslate.content);
                    setOverlayLoading(false);
                } else {
                    void translateSection(sectionToTranslate);
                }
            } else {
                setShowTranslateOverlay(false);
                setOverlayClosed(true);
            }
        }
    }, [showTranslateOverlay, queryLanguage, sections, location.search]);

    const translateSection = useCallback(
        async (s: Section) => {
            const targetLang = queryLanguage || queryParams.get('language') || 'yoruba';
            setOverlayLoading(true);
            setOverlayError(null);
            try {
                const { data: resp } = await axios.post(`${API_BASE}/translation/translate`, {
                    text: s.content,
                    targetLanguage: targetLang,
                });
                const translated = resp?.response?.data?.translated_text || '';
                setOverlayTranslations((prev) => {
                    if (typeof prev === 'string') return { [s.section]: translated };
                    return { ...prev, [s.section]: translated };
                });
            } catch {
                setOverlayError('Failed to translate section');
            } finally {
                setOverlayLoading(false);
            }
        },
        [queryLanguage],
    );

    const applyTranslationsToPage = () => {
        if (!overlayTranslations || Object.keys(overlayTranslations).length === 0) return;
        setSections((prev) =>
            prev.map((s) => {
                if (typeof overlayTranslations === 'string') return { ...s, content: overlayTranslations };
                return { ...s, content: overlayTranslations[s.section] || s.content };
            }),
        );
        setShowTranslateOverlay(false);
        setOverlayClosed(true);
    };

    if (loading) {
        return (
            <div className="flex h-dvh w-full flex-col bg-white">
                <div className="flex flex-1 items-center justify-center">
                    <div className="text-lg text-gray-600">Loading document...</div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="flex h-dvh flex-col bg-white">
                <div className="flex flex-1 items-center justify-center">
                    <div className="text-lg text-red-600">Error: {error}</div>
                </div>
            </div>
        );
    }

    return (
        <div className="flex h-dvh w-full flex-col bg-[#f5f5f5]">
            <TranslateOverlay
                isOpen={showTranslateOverlay && !overlayClosed}
                targetSectionId={queryParams.get('section')}
                queryLanguage={queryLanguage}
                loading={overlayLoading}
                error={overlayError}
                translations={overlayTranslations}
                sections={sections}
                onClose={() => {
                    setShowTranslateOverlay(false);
                    setOverlayClosed(true);
                }}
                onApply={applyTranslationsToPage}
                onTranslate={(s: Section) => void translateSection(s)}
            />

            <div ref={scrollContainerRef} className="relative flex-1 overflow-y-auto">
                <div className="sticky top-0 z-10 backdrop-blur-sm">
                    <div className="mx-auto max-w-[900px] bg-white/90 px-6 py-6">
                        <div className="mb-4 flex justify-center">
                            <img src="/logos/logo.svg" alt="Podus AI" className="w-20 sm:w-24 lg:w-32" />
                        </div>

                        <div className="mb-2 mt-6 space-y-2 sm:space-y-0">
                            <h1 className="text-center text-sm font-light italic text-black sm:text-left">
                                {documentTitle}
                            </h1>
                        </div>

                        <div className="flex h-1 w-full">
                            <div className="w-1/6 bg-orange-500" />
                            <div className="w-1/3 bg-gray-800" />
                            <div className="w-1/4 bg-gray-300" />
                            <div className="w-1/6 bg-orange-500" />
                            <div className="flex-1 bg-gray-800" />
                        </div>
                    </div>
                </div>

                <div className="mx-auto max-w-[900px] bg-white px-6 py-6">
                    <div className="space-y-4 leading-relaxed text-gray-800 sm:space-y-6">
                        {sections.map((section) => (
                            <div
                                key={section.section}
                                ref={(el) => {
                                    if (el) sectionRefs.current[section.section] = el;
                                }}
                            >
                                <h2 className="mb-3 flex items-center justify-between gap-3 text-base font-semibold text-black sm:mb-4 sm:text-lg lg:text-xl">
                                    Section {section.section}:
                                </h2>
                                <div className="prose prose-sm max-w-none text-justify lg:prose-base">
                                    {(() => {
                                        const processedContent = section.content
                                            .replace(/(\n|^)\((\d+)\)/g, '\n\n**($2)** ')
                                            .replace(/(\n|^)([A-Z][a-z]+):/g, '\n\n**$2:** ')
                                            .replace(/\n\n\n+/g, '\n\n');
                                        return <MarkdownRenderer content={processedContent} />;
                                    })()}
                                </div>
                            </div>
                        ))}
                    </div>

                    <div className="mt-12 border-t border-gray-200 pt-8 text-center">
                        <div className="mb-4 text-sm text-gray-600">
                            <span>By using this document, you agree to our </span>
                            <a
                                href="https://help.podus.org/privacy"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="font-medium text-primary hover:underline"
                            >
                                Privacy Policy
                            </a>
                            <span> and </span>
                            <a
                                href="https://help.podus.org/terms"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="font-medium text-primary hover:underline"
                            >
                                Terms & Conditions
                            </a>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
